import { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, PassTypeBadge, formatDate, formatDateTime } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { getCoordinatorPending, getCoordinatorHistory, coordinatorAction } from "../lib/api";
import { Clock, CheckCircle2, XCircle, Eye, Loader2, AlertCircle } from "lucide-react";
import TodayPastPasses from "../components/TodayPastPasses";

const CoordinatorDashboard = () => {
  return (
    <DashboardLayout title="Class Coordinator Portal">
      <Routes>
        <Route index element={<CoordinatorHome />} />
      </Routes>
    </DashboardLayout>
  );
};

const CoordinatorHome = () => {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pendingRes, historyRes] = await Promise.all([
        getCoordinatorPending(),
        getCoordinatorHistory()
      ]);
      setPending(pendingRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (actionType === "reject" && !rejectionReason.trim()) {
      toast.error("Rejection reason is required");
      return;
    }

    setSubmitting(true);
    try {
      await coordinatorAction(selectedPass.id, {
        action: actionType,
        remarks: remarks.trim() || null,
        rejection_reason: actionType === "reject" ? rejectionReason : null
      });
      toast.success(actionType === "approve" ? "Request approved!" : "Request rejected");
      setSelectedPass(null);
      setActionType(null);
      setRemarks("");
      setRejectionReason("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  const stats = {
    pending: pending.length,
    approved: history.filter(h => h.approval_history?.some(a => a.approver_id && a.action === "approve")).length,
    rejected: history.filter(h => h.approval_history?.some(a => a.approver_id && a.action === "reject")).length,
  };

  return (
    <div className="space-y-6" data-testid="coordinator-dashboard">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Approval</p>
                <p className="text-2xl font-semibold text-amber-600">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-semibold text-emerald-600">{stats.approved}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Rejected</p>
                <p className="text-2xl font-semibold text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="pending-tab">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Pending Approvals</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : pending.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-300" />
                  <p>No pending requests!</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request No.</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Exit Date</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pending.map((req) => (
                      <TableRow key={req.id} data-testid={`pending-row-${req.id}`}>
                        <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{req.student_name}</p>
                            <p className="text-xs text-slate-500">{req.student_enrollment}</p>
                          </div>
                        </TableCell>
                        <TableCell><PassTypeBadge type={req.pass_type} /></TableCell>
                        <TableCell>{formatDate(req.exit_date)}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{req.destination}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedPass(req); setActionType(null); }}
                              data-testid={`view-btn-${req.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => { setSelectedPass(req); setActionType("approve"); }}
                              data-testid={`approve-btn-${req.id}`}
                            >
                              Approve
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => { setSelectedPass(req); setActionType("reject"); }}
                              data-testid={`reject-btn-${req.id}`}
                            >
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Previous Actions</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {history.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <p>No history yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request No.</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                        <TableCell>{req.student_name}</TableCell>
                        <TableCell><PassTypeBadge type={req.pass_type} /></TableCell>
                        <TableCell><StatusBadge status={req.status} /></TableCell>
                        <TableCell>{formatDate(req.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={!!selectedPass && !!actionType} onOpenChange={() => { setSelectedPass(null); setActionType(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {actionType === "approve" ? "Approve Request" : "Reject Request"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPass && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <p><span className="text-slate-500">Request:</span> {selectedPass.request_number}</p>
                <p><span className="text-slate-500">Student:</span> {selectedPass.student_name}</p>
                <p><span className="text-slate-500">Reason:</span> {selectedPass.reason}</p>
                <p><span className="text-slate-500">Exit:</span> {formatDate(selectedPass.exit_date)} at {selectedPass.exit_time}</p>
                <p><span className="text-slate-500">Destination:</span> {selectedPass.destination}</p>
              </div>

              {actionType === "reject" && (
                <div className="space-y-2">
                  <Label htmlFor="rejection_reason" className="text-red-600">
                    Rejection Reason *
                  </Label>
                  <Textarea
                    id="rejection_reason"
                    placeholder="Enter the reason for rejection (required)"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    data-testid="rejection-reason-input"
                    className="border-red-200 focus:border-red-400"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="remarks">Remarks (Optional)</Label>
                <Textarea
                  id="remarks"
                  placeholder="Add any remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  data-testid="remarks-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedPass(null); setActionType(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={submitting}
              className={actionType === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              variant={actionType === "reject" ? "destructive" : "default"}
              data-testid="confirm-action-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : actionType === "approve" ? (
                "Confirm Approval"
              ) : (
                "Confirm Rejection"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Only Dialog */}
      <Dialog open={!!selectedPass && !actionType} onOpenChange={() => setSelectedPass(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gate Pass Details</DialogTitle>
          </DialogHeader>
          {selectedPass && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Request Number</p>
                  <p className="font-mono">{selectedPass.request_number}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <StatusBadge status={selectedPass.status} />
                </div>
                <div>
                  <p className="text-slate-500">Student Name</p>
                  <p className="font-medium">{selectedPass.student_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Enrollment</p>
                  <p>{selectedPass.student_enrollment}</p>
                </div>
                <div>
                  <p className="text-slate-500">Course / Year</p>
                  <p>{selectedPass.course_name} - Year {selectedPass.year}</p>
                </div>
                <div>
                  <p className="text-slate-500">Pass Type</p>
                  <PassTypeBadge type={selectedPass.pass_type} />
                </div>
                <div>
                  <p className="text-slate-500">Exit Date & Time</p>
                  <p>{formatDate(selectedPass.exit_date)} at {selectedPass.exit_time}</p>
                </div>
                <div>
                  <p className="text-slate-500">Return Date & Time</p>
                  <p>{formatDate(selectedPass.expected_return_date)} at {selectedPass.expected_return_time}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Destination</p>
                  <p>{selectedPass.destination}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Reason</p>
                  <p>{selectedPass.reason}</p>
                </div>
                <div>
                  <p className="text-slate-500">Parent Name</p>
                  <p>{selectedPass.parent_name || "-"}</p>
                </div>
                <div>
                  <p className="text-slate-500">Parent Phone</p>
                  <p>{selectedPass.parent_phone || "-"}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Gate Pass Activity - at bottom */}
      <TodayPastPasses showPrint={true} />
    </div>
  );
};

export default CoordinatorDashboard;
